import { useEffect, useRef, useState } from 'react';
import { ReactEditor } from 'slate-react';

const STILL = { scrolled: false, away: false };

function findScroller(element) {
  let current = element.parentElement;
  while (current !== null) {
    const overflow = window.getComputedStyle(current).overflowY;
    if (overflow === 'auto' || overflow === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

// A header someone is typing in stays in view.
function isEditing(wrapper) {
  const selection = window.getSelection();
  return selection !== null && selection.anchorNode !== null && wrapper.contains(selection.anchorNode);
}

function findSurface(editor, element) {
  try {
    return ReactEditor.toDOMNode(editor, element);
  } catch (exception) {
    return null;
  }
}

// The header's height lets the next block slide under it; the buttons' width keeps the links on the
// right of a centered logo clear of them.
function observeSizes(surface) {
  const wrapper = surface.closest('.site-nav-node');
  function measure() {
    const actions = surface.querySelector('.site-nav-actions');
    wrapper.style.setProperty('--site-nav-editor-height', wrapper.offsetHeight + 'px');
    if (actions !== null) {
      surface.style.setProperty('--site-nav-actions-width', actions.offsetWidth + 'px');
    }
  }
  const observer = new ResizeObserver(measure);
  observer.observe(wrapper);
  for (const actions of surface.querySelectorAll('.site-nav-actions')) {
    observer.observe(actions);
  }
  return observer;
}

// The editor scrolls inside its own container, so the page's first header follows that container the way
// the published header follows the page: it changes after the threshold, and a sticky one stays at the
// top and can hide while scrolling down.
function followScroll(wrapper, scroller, value, setState) {
  let last = scroller.scrollTop;
  let current = STILL;
  function update() {
    const position = scroller.scrollTop;
    const difference = position - last;
    const scrolled = position > value.threshold;
    let away = current.away && value.positioning === 'sticky';
    if (Math.abs(difference) >= 8) {
      away = value.positioning === 'sticky' && value.visibility === 'scroll' && scrolled && difference > 0 && isEditing(wrapper) === false;
      last = position;
    }
    if (scrolled !== current.scrolled || away !== current.away) {
      current = { scrolled, away };
      setState(current);
    }
  }
  scroller.addEventListener('scroll', update, { passive: true });
  update();
  return update;
}

export function useEditorScroll(editor, element, value, primary) {

  const [state, setState] = useState(STILL);
  const elementReference = useRef(element);

  elementReference.current = element;

  const threshold = value.threshold;
  const positioning = value.positioning;
  const visibility = value.visibility;

  useEffect(function trackHeader() {

    const surface = findSurface(editor, elementReference.current);

    if (surface === null) {
      return undefined;
    }

    const wrapper = surface.closest('.site-nav-node');
    const scroller = findScroller(wrapper);
    const observer = observeSizes(surface);
    let update = null;

    if (primary === true && scroller !== null) {
      update = followScroll(wrapper, scroller, { threshold, positioning, visibility }, setState);
    } else {
      setState(STILL);
    }

    return function stopTracking() {
      observer.disconnect();
      if (update !== null) {
        scroller.removeEventListener('scroll', update);
      }
    };

  }, [editor, primary, threshold, positioning, visibility]);

  return state;

}
